import { Component } from '@theme/component';

/**
 * @typedef {object} CountdownRefs
 * @property {HTMLElement} [running] - Wrapper shown while the countdown runs.
 * @property {HTMLElement} [ended] - Message shown once the end time has passed.
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 */

/**
 * Ticks a server-rendered countdown once per second and swaps to the ended message
 * when the end time (`data-end`, Unix seconds) passes.
 *
 * @extends {Component<CountdownRefs>}
 */
class CountdownTimer extends Component {
  /** @type {number | undefined} */
  #intervalId;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
  }

  #start() {
    this.#stop();

    const end = Number(this.dataset.end);
    if (!Number.isFinite(end) || end <= 0 || this.hasAttribute('data-ended')) return;

    this.#tick(end);
    this.#intervalId = window.setInterval(() => this.#tick(end), 1000);
  }

  #stop() {
    if (this.#intervalId !== undefined) {
      window.clearInterval(this.#intervalId);
      this.#intervalId = undefined;
    }
  }

  /**
   * @param {number} end - End time in Unix seconds.
   */
  #tick(end) {
    const remaining = Math.max(0, Math.floor(end - Date.now() / 1000));

    if (remaining === 0) {
      this.#stop();
      this.setAttribute('data-ended', '');
      if (this.refs.running) this.refs.running.hidden = true;
      if (this.refs.ended) this.refs.ended.hidden = false;
      return;
    }

    const parts = {
      days: Math.floor(remaining / 86400),
      hours: Math.floor((remaining % 86400) / 3600),
      minutes: Math.floor((remaining % 3600) / 60),
      seconds: remaining % 60,
    };

    for (const [unit, value] of Object.entries(parts)) {
      const element = this.refs[/** @type {keyof CountdownRefs} */ (unit)];
      const text = String(value).padStart(2, '0');
      if (element && element.textContent !== text) element.textContent = text;
    }
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
